/**
 * Environment utilities for checking deployment and build modes.
 *
 * IMPORTANT: There are THREE different "mode" concepts:
 *
 * 1. PRODUCTION_MODE (Branch/Deployment):
 *    - Controlled by GitHub Actions Environments
 *    - development branch → PRODUCTION_MODE=false
 *    - main branch → PRODUCTION_MODE=true
 *    - Affects: Feature flags, destructive operations, dev UI
 *
 * 2. Vite Build Mode:
 *    - npm run dev → DEV=true (hot reload, unminified)
 *    - npm run build → PROD=true (minified, optimized)
 *    - Affects: Logging defaults, source maps
 *
 * 3. Log Level:
 *    - Controlled by LOG_LEVEL / VITE_LOG_LEVEL
 *    - Affects: Verbosity of logging output
 *
 * These modes are INDEPENDENT! A production build can exist in the development branch.
 *
 * @see .lovable/memory/technical/environment-modes-policy.md
 */

import { logger } from "./logger";
import { invokeWithRetry } from "./invokeWithRetry";

// Cache for production mode status (fetched from backend)
let cachedProductionMode: boolean | null = null;
let fetchPromise: Promise<boolean> | null = null;

/**
 * Check if Vite dev server is running (not a production build).
 * This is DIFFERENT from PRODUCTION_MODE - a production build
 * can exist in the development branch.
 */
export function isViteDevServer(): boolean {
  return import.meta.env.DEV === true;
}

/**
 * Check if this is a Vite production build.
 */
export function isViteProdBuild(): boolean {
  return import.meta.env.PROD === true;
}

/**
 * Get the PRODUCTION_MODE status from the backend (async).
 * This determines if we're deployed from main (production) or development branch.
 *
 * Results are cached for the session.
 *
 * @returns true if running in production mode (main branch), false otherwise
 */
export async function isProductionMode(): Promise<boolean> {
  // Return cached result if available
  if (cachedProductionMode !== null) {
    return cachedProductionMode;
  }

  // Reuse existing fetch if in progress
  if (fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = (async () => {
    try {
      // invokeWithRetry only depends on the supabase client + logger (no cycle back to
      // environment.ts), and it is already statically imported across the app — a dynamic
      // import here split nothing (INEFFECTIVE_DYNAMIC_IMPORT), so use a static import.
      const { data, error } = await invokeWithRetry<{ isProductionMode?: boolean; error?: string }>(
        "check-dev-mode",
      );
      if (error) throw error;
      if (!data || data.error) throw new Error(data?.error || "check-dev-mode returned no data");
      cachedProductionMode = data.isProductionMode ?? false;
      logger.debug("Production mode status fetched", { isProductionMode: cachedProductionMode });
      return cachedProductionMode;
    } catch (error) {
      logger.warn("Failed to fetch production mode status, will retry on next call", error);
      // Don't cache false — let the next call retry instead of locking the session into dev mode
      return false;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

/**
 * Synchronous check using cached value.
 * Use this when you can't await, but call isProductionMode() first to warm the cache.
 *
 * @returns cached production mode value, or false if not yet fetched
 */
export function isProductionModeSync(): boolean {
  return cachedProductionMode ?? false;
}

/**
 * Check if cache is already populated.
 * Useful for conditional async fetching.
 */
export function isProductionModeCached(): boolean {
  return cachedProductionMode !== null;
}

/**
 * Reset cached production mode (for testing purposes).
 */
export function resetProductionModeCache(): void {
  cachedProductionMode = null;
  fetchPromise = null;
}
